/**
 * @architecture-header 1
 * @layer module
 * @domain support
 * @responsibility Load the caller's own support cases, one case's full detail, and (owner/admin
 *   only) an Organization's support cases through TanStack Query.
 * @state-owner TanStack Query
 * @allowed-imports app environment; TanStack Query; zod; same-module types
 * @forbidden routes; app bootstrap; direct Supabase client; Zustand entity copies
 * @public-api via modules/support/index.ts
 * @side-effects authenticated GET request outside demo mode
 * @naming-hook GET -> use<Resource> (no verb); POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-path plural lowercase nouns; /me for the caller; no version, role or verb segment; filters in the query string
 * @naming-body camelCase; never the caller's role and never a server-side key — the token says who, the path says what
 * @naming-rules docs/architecture/API_NAMING.md, all three enforced by pnpm api-naming:check
 * @tests workspace Playwright flows in frontend/apps/web/e2e/sh-f07.spec.ts
 */

import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { useWorkspaceEnvironment } from '../../../app/config/environment';
import { useActiveRole } from '../../../app/providers/ActiveRoleProvider';

export const supportCaseStatusSchema = z.enum(['open', 'in_progress', 'needs_info', 'resolved']);
export type SupportCaseStatus = z.infer<typeof supportCaseStatusSchema>;

export const supportCaseMessageSchema = z.object({
  authorId: z.string(),
  authorRole: z.literal('reporter'),
  body: z.string(),
  createdAt: z.string(),
});
export type SupportCaseMessage = z.infer<typeof supportCaseMessageSchema>;

export const supportCaseSchema = z.object({
  id: z.string(),
  subject: z.string(),
  status: supportCaseStatusSchema,
  revision: z.number().int().positive(),
  createdAt: z.string(),
  reporterUserId: z.string(),
  messages: z.array(supportCaseMessageSchema),
});
export type SupportCase = z.infer<typeof supportCaseSchema>;

export const supportCaseSummarySchema = z.object({
  id: z.string(),
  subject: z.string(),
  status: supportCaseStatusSchema,
  revision: z.number().int().positive(),
  createdAt: z.string(),
  updatedAt: z.string(),
  latestMessagePreview: z.string(),
});
export type SupportCaseSummary = z.infer<typeof supportCaseSummarySchema>;

export const organizationSupportCaseSummarySchema = supportCaseSummarySchema.extend({
  reporterUserId: z.string(),
  reporterDisplayName: z.string(),
});
export type OrganizationSupportCaseSummary = z.infer<typeof organizationSupportCaseSummarySchema>;

const listResponseSchema = z.object({ items: z.array(supportCaseSummarySchema) });
const organizationListResponseSchema = z.object({
  items: z.array(organizationSupportCaseSummarySchema),
});

export const supportCaseKeys = {
  mine: () => ['support-cases', 'mine'] as const,
  detail: (caseId: string) => ['support-cases', 'detail', caseId] as const,
  organization: (organizationId: string) =>
    ['support-cases', 'organization', organizationId] as const,
};

export function useMySupportCases() {
  const environment = useWorkspaceEnvironment();
  return useQuery({
    queryKey: supportCaseKeys.mine(),
    queryFn: async (): Promise<SupportCaseSummary[]> => {
      if (environment.demoMode) return [];
      return listResponseSchema.parse(
        await environment.apiClient.request<unknown>('/api/v1/support/cases?status=all'),
      ).items;
    },
  });
}

export function useSupportCase(caseId: string | null) {
  const environment = useWorkspaceEnvironment();
  return useQuery({
    queryKey: supportCaseKeys.detail(caseId ?? ''),
    enabled: caseId !== null,
    queryFn: async (): Promise<SupportCase> => {
      if (!caseId) throw new Error('caseId is required');
      if (environment.demoMode) {
        // Demo mode has no persisted backend -- the only cases that can ever be opened are ones
        // this session already created or replied to, both of which populate this exact cache
        // entry directly (see support-cases.mutations.ts). A cache miss here is a genuine, honest
        // "not found" (never fabricated), matching the real API's own non-disclosure 404 shape.
        throw new Error('SUPPORT_CASE_NOT_FOUND');
      }
      return supportCaseSchema.parse(
        await environment.apiClient.request<unknown>(
          `/api/v1/support/cases/${encodeURIComponent(caseId)}`,
        ),
      );
    },
  });
}

export function useOrganizationSupportCases(organizationId: string | null) {
  const environment = useWorkspaceEnvironment();
  const role = useActiveRole();
  return useQuery({
    queryKey: supportCaseKeys.organization(organizationId ?? ''),
    enabled: organizationId !== null,
    queryFn: async (): Promise<OrganizationSupportCaseSummary[]> => {
      if (environment.demoMode) {
        // No live backend to authorize against locally -- approximate the real owner/admin gate
        // with this session's own role (teacher accounts are this app's demo org owners; student
        // accounts are members), so the modal's tab-visibility gating (round-1 finding 1) has a
        // real success/error split to react to, the same shape a real 403 produces.
        if (role !== 'teacher') {
          throw Object.assign(new Error('Organization admin access is required.'), { status: 403 });
        }
        return [];
      }
      const encodedOrganizationId = encodeURIComponent(organizationId ?? '');
      return organizationListResponseSchema.parse(
        await environment.apiClient.request<unknown>(
          `/api/v1/organizations/${encodedOrganizationId}/support/cases?status=all`,
        ),
      ).items;
    },
  });
}
